import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import Engine from './engine';

/**
 * Live Tester overlay.
 *
 * Opened from the launch page ("Open with RapidChat") as /tester/live/<appName>.
 * It renders the deployed app full-screen in a same-origin iframe (/apps/<name>/)
 * and floats a draggable RapidChat icon (lower-left by default). Clicking the icon
 * opens a popup with the same record / replay / save-test-case functionality as the
 * Admin → Tester Lab, but driven live against the running app.
 *
 * 100% client-side: the record/replay engine (client/src/tester-widget/engine) is
 * framework-agnostic CJS, default-imported so webpack's interop yields the whole
 * { Recorder, Replayer } surface. Nothing here touches the server.
 *
 * Why the overlay chrome doesn't pollute recordings: the Recorder attaches its
 * capture-phase listeners to the *iframe's* document, while the icon + popup live
 * in the parent document. They are separate DOM trees, so the tester's clicks on
 * our own UI are never seen by the recorder — no ignore-selector gymnastics needed.
 */

const { Recorder, Replayer } = Engine;
const LS_KEY = 'lyric.testerLive.cases.v1';
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function loadCases() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; }
}
function persistCases(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* quota / disabled */ }
}

// ---- turn recorded engine steps into plain-English reproduction lines --------
const quoteVal = (v) => `"${String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, 80)}"`;

// A friendly name for the element a step acted on, derived from the locator
// descriptor the recorder captured (text → aria-label → name → testid → id → tag).
function targetLabel(t) {
    if (!t) return 'the element';
    const q = (s) => `"${String(s).replace(/\s+/g, ' ').trim().slice(0, 60)}"`;
    if (t.text) return q(t.text);
    if (t.ariaLabel) return q(t.ariaLabel);
    if (t.name) return q(t.name);
    if (t.testid) return q(t.testid);
    if (t.id) return q('#' + t.id);
    const tag = t.tag || 'element';
    const noun = tag === 'a' ? 'link'
        : tag === 'input' ? 'field'
            : tag === 'select' ? 'dropdown'
                : tag === 'textarea' ? 'text area'
                    : tag;
    return `the ${noun}`;
}

function humanStep(step) {
    const t = step.target || {};
    const name = targetLabel(t);
    switch (step.action) {
        case 'fill': return `Enter ${quoteVal(step.value)} in ${name}`;
        case 'check': return `${step.value ? 'Check' : 'Uncheck'} ${name}`;
        case 'select': return `Choose ${quoteVal(step.value)} in ${name}`;
        case 'navigate': return `Navigate to ${step.url}`;
        case 'click':
        default: return `Click ${name}`;
    }
}

// Render steps as a numbered list starting at `startNum` (so recordings can be
// appended after any steps the user already typed).
function humanizeSteps(steps, startNum) {
    let n = startNum || 1;
    return (steps || []).map((s) => `${n++}. ${humanStep(s)}`).join('\n');
}

// How many leading "1. …" style lines already exist, so appended recordings
// continue the numbering instead of restarting at 1.
function countNumberedLines(text) {
    if (!text) return 0;
    return text.split('\n').filter((l) => /^\s*\d+\./.test(l)).length;
}

export default function LiveTesterOverlay() {
    const { appName } = useParams();
    const navigate = useNavigate();
    const { oktaAuth, authState } = useOktaAuth() || {};

    const [authz, setAuthz] = useState('checking'); // 'checking' | 'ok' | 'denied'
    const [panelOpen, setPanelOpen] = useState(false);
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [steps, setSteps] = useState([]);
    const [report, setReport] = useState([]);
    const [status, setStatus] = useState('idle');
    const [cases, setCases] = useState(loadCases);
    const [caseName, setCaseName] = useState('');
    const [showSteps, setShowSteps] = useState(false);

    // ---- Ask-AI state ------------------------------------------------------
    const [aiSelection, setAiSelection] = useState(''); // text grabbed from the app
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiMsgs, setAiMsgs] = useState([]); // { role:'user'|'ai', text, ctx?, err? }
    const [aiBusy, setAiBusy] = useState(false);
    const [aiError, setAiError] = useState('');
    const aiThreadRef = useRef(null);

    // ---- Report-a-bug state ------------------------------------------------
    const [bugOpen, setBugOpen] = useState(false);
    const [bugProjects, setBugProjects] = useState([]);
    const [bugProjectsLoaded, setBugProjectsLoaded] = useState(false);
    const [bugProject, setBugProject] = useState('');
    const [bugFilter, setBugFilter] = useState('');
    const [bugListOpen, setBugListOpen] = useState(false);
    const [bugSummary, setBugSummary] = useState('');
    const [bugDescription, setBugDescription] = useState('');
    const [bugSteps, setBugSteps] = useState('');
    const [bugPriority, setBugPriority] = useState('Medium');
    const [bugIncludeCtx, setBugIncludeCtx] = useState(true);
    const [bugBusy, setBugBusy] = useState(false);
    const [bugError, setBugError] = useState('');
    const [bugResult, setBugResult] = useState(null); // { key, link }
    const [bugRecording, setBugRecording] = useState(false); // recording steps into the bug form

    // ---- Replay-failure overlay --------------------------------------------
    // { label, steps, failed } when the most recent replay had failing step(s);
    // null otherwise. Lets the tester file a bug directly from the failure.
    const [failureOverlay, setFailureOverlay] = useState(null);

    const iframeRef = useRef(null);
    const recorderRef = useRef(null);
    const bugRecorderRef = useRef(null);
    const fileRef = useRef(null);

    const src = `/apps/${appName}/`;

    // ---- access gate: owner / co-admin / tester / Lyric admin (LIVE per app) --
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = authState?.accessToken?.accessToken;
                const res = await fetch(`/api/apps/${encodeURIComponent(appName)}/tester-access`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                const body = await res.json().catch(() => ({}));
                if (!cancelled) setAuthz(res.ok && body.allowed ? 'ok' : 'denied');
            } catch {
                if (!cancelled) setAuthz('denied');
            }
        })();
        return () => { cancelled = true; };
    }, [authState, appName]);

    // ---- draggable floating icon -------------------------------------------
    const [pos, setPos] = useState(() => ({
        left: 24,
        top: (typeof window !== 'undefined' ? window.innerHeight - 96 : 600),
    }));
    const drag = useRef({ down: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

    const onPointerDown = (e) => {
        const d = drag.current;
        d.down = true; d.moved = false;
        d.sx = e.clientX; d.sy = e.clientY;
        d.ox = e.clientX - pos.left; d.oy = e.clientY - pos.top;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    };
    const onPointerMove = (e) => {
        const d = drag.current;
        if (!d.down) return;
        if (Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 4) d.moved = true;
        setPos({
            left: clamp(e.clientX - d.ox, 4, window.innerWidth - 56),
            top: clamp(e.clientY - d.oy, 4, window.innerHeight - 56),
        });
    };
    const onPointerUp = () => {
        const d = drag.current;
        if (!d.down) return;
        d.down = false;
        if (!d.moved) setPanelOpen((o) => !o); // treat a non-drag as a click
    };

    // ---- engine glue (iframe target) ---------------------------------------
    function getWinDoc() {
        const f = iframeRef.current;
        if (!f) throw new Error('app frame not ready');
        let win;
        let doc;
        try {
            win = f.contentWindow;
            doc = f.contentDocument || (win && win.document);
        } catch {
            throw new Error('Cannot reach the app — it appears to be cross-origin or blocks framing (X-Frame-Options).');
        }
        if (!doc || !doc.body) throw new Error('The app has not finished loading yet. Give it a moment or use "Reload app".');
        return { win, doc };
    }

    function reloadIframe() {
        return new Promise((resolve) => {
            const f = iframeRef.current;
            if (!f) return resolve();
            let settled = false;
            const finish = () => { if (settled) return; settled = true; f.removeEventListener('load', finish); resolve(); };
            f.addEventListener('load', finish);
            try {
                if (f.contentWindow) f.contentWindow.location.reload();
                else { const s = f.src; f.src = 'about:blank'; setTimeout(() => { f.src = s; }, 0); }
            } catch {
                const s = f.src; f.src = 'about:blank'; setTimeout(() => { f.src = s; }, 0);
            }
            setTimeout(finish, 4000); // safety net if 'load' never fires
            return undefined;
        });
    }

    function startRecording() {
        try {
            const { win, doc } = getWinDoc();
            const rec = new Recorder({ win, doc });
            rec.start();
            recorderRef.current = rec;
            setSteps([]);
            setReport([]);
            setRecording(true);
            setStatus('recording — interact with the app…');
        } catch (e) {
            setStatus('cannot record: ' + e.message);
        }
    }

    function stopRecording() {
        const rec = recorderRef.current;
        if (!rec) return;
        const s = rec.stop();
        recorderRef.current = null;
        setSteps(s);
        setRecording(false);
        setStatus(`recorded ${s.length} step(s)`);
    }

    async function replay(stepsToRun, label) {
        const run = stepsToRun || steps;
        if (!run.length) { setStatus('nothing to replay — record or load a case first'); return; }
        setBusy(true);
        setReport([]);
        setFailureOverlay(null);
        setStatus('reloading app…');
        try {
            await reloadIframe();
            const { win, doc } = getWinDoc();
            const replayer = new Replayer({
                win,
                doc,
                timeout: 5000,
                stepDelay: 250,
                onStep: (r) => setStatus(`step ${r.index} · ${r.action} ${r.ok ? '✓' : '✗'}`),
            });
            const rep = await replayer.play(run, { failFast: false });
            setReport(rep);
            const ok = rep.filter((r) => r.ok).length;
            setStatus(`replay complete — ${ok}/${rep.length} step(s) ok`);
            const failed = rep.filter((r) => !r.ok);
            if (failed.length > 0) {
                setFailureOverlay({ label: label || caseName || 'this test', steps: run, failed });
            }
        } catch (e) {
            setStatus('replay failed: ' + e.message);
        } finally {
            setBusy(false);
        }
    }

    function reloadApp() {
        setReport([]);
        setStatus('reloading app…');
        reloadIframe().then(() => setStatus('app reloaded'));
    }

    // Wipe the current recording (steps + last replay report) without tearing
    // down the overlay — lets the tester start a fresh test case in place.
    function clearSteps() {
        if (recording) { setStatus('stop recording before clearing'); return; }
        setSteps([]);
        setReport([]);
        setCaseName('');
        setStatus('recorded steps cleared');
    }

    // ---- test cases (localStorage, per app) --------------------------------
    const myCases = cases.filter((c) => c.appName === appName);

    function saveCase() {
        if (!steps.length) { setStatus('record something before saving'); return; }
        const name = (caseName || '').trim() || `Case ${new Date().toLocaleString()}`;
        const c = {
            id: 'tc_' + Date.now().toString(36),
            name,
            appName,
            steps,
            createdAt: new Date().toISOString(),
        };
        const next = [c, ...cases];
        setCases(next);
        persistCases(next);
        setCaseName('');
        setStatus(`saved "${name}" (${steps.length} steps)`);
    }

    function loadCase(c) {
        setSteps(c.steps || []);
        setReport([]);
        setStatus(`loaded "${c.name}" (${(c.steps || []).length} steps) — press ▶ Replay`);
    }

    function deleteCase(id) {
        const next = cases.filter((c) => c.id !== id);
        setCases(next);
        persistCases(next);
    }

    function exportCases() {
        const blob = new Blob([JSON.stringify(cases, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'lyric-live-tester-cases.json';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    function importCases(file) {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(String(reader.result));
                if (!Array.isArray(parsed)) throw new Error('expected an array of cases');
                // Retag every imported case to the app that's currently open — otherwise
                // a case exported from (or authored under) a different appName silently
                // lands in localStorage but never shows up in this app's "saved tests"
                // list (which filters by appName), making it look like the import did
                // nothing.
                let retagged = 0;
                const byId = new Map(cases.map((c) => [c.id, c]));
                for (const c of parsed) {
                    if (!c || !c.id) continue;
                    if (c.appName && c.appName !== appName) retagged += 1;
                    byId.set(c.id, { ...c, appName });
                }
                const next = Array.from(byId.values());
                setCases(next);
                persistCases(next);
                setStatus(`imported — ${parsed.length} case(s)${retagged ? ` (${retagged} retagged to this app)` : ''}`);
            } catch (e) {
                setStatus('import failed: ' + e.message);
            }
        };
        reader.readAsText(file);
    }

    function exitTester() {
        // Opened via window.open from the launch page → try to close the tab,
        // otherwise fall back to navigating home within the SPA.
        window.close();
        setTimeout(() => navigate('/'), 60);
    }

    // ---- Ask AI ------------------------------------------------------------
    async function getToken() {
        try {
            if (oktaAuth && oktaAuth.getAccessToken) {
                const t = await oktaAuth.getAccessToken();
                if (t) return t;
            }
        } catch { /* fall through to cached token */ }
        return (authState && authState.accessToken && authState.accessToken.accessToken) || '';
    }

    // Read the current text selection from inside the app iframe (same-origin).
    function readAppSelection() {
        try {
            const { win } = getWinDoc();
            const s = win.getSelection ? String(win.getSelection()) : '';
            return s.trim();
        } catch {
            return '';
        }
    }

    function grabSelection() {
        const s = readAppSelection();
        if (s) { setAiSelection(s); setAiError(''); }
        else setAiError('No text selected in the app — highlight something first, then click “Use selection”.');
    }

    async function askAi() {
        const question = aiPrompt.trim();
        // If the user hasn't explicitly grabbed a selection, pick up whatever is
        // currently highlighted in the app so "select then Ask" just works.
        let ctx = aiSelection.trim() || readAppSelection();
        if (!question && !ctx) {
            setAiError('Type a question — or select text in the app — first.');
            return;
        }
        const userText = question || 'Please review the selected text.';
        setAiBusy(true);
        setAiError('');
        setAiMsgs((m) => [...m, { role: 'user', text: userText, ctx }]);
        try {
            const token = await getToken();
            const context = ctx ? `Selected text from the app "${appName}":\n"""\n${ctx}\n"""` : '';
            const res = await fetch('/api/ai/ask', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ message: userText, context }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || body.success === false) {
                throw new Error(body.error || `AI request failed (HTTP ${res.status})`);
            }
            setAiMsgs((m) => [...m, { role: 'ai', text: body.response || '(no response)' }]);
            setAiPrompt('');
        } catch (e) {
            setAiError(e.message);
            setAiMsgs((m) => [...m, { role: 'ai', text: '⚠️ ' + e.message, err: true }]);
        } finally {
            setAiBusy(false);
        }
    }

    // Keep the chat scrolled to the newest message.
    useEffect(() => {
        const el = aiThreadRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [aiMsgs, aiBusy]);

    // ---- Report a bug ------------------------------------------------------
    function currentAppUrl() {
        try {
            const f = iframeRef.current;
            const href = f && f.contentWindow && f.contentWindow.location && f.contentWindow.location.href;
            if (href && href !== 'about:blank') return href;
        } catch { /* cross-origin — fall back to the mount path */ }
        return src;
    }

    async function loadProjects() {
        setBugError('');
        try {
            const token = await getToken();
            const res = await fetch('/api/projects', {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const body = await res.json().catch(() => ({}));
            if (body && body.requiresAuth) {
                setBugError('JIRA sign-in required — open the main Lyric app to connect JIRA, then retry.');
                return;
            }
            if (!res.ok || body.success === false) {
                throw new Error(body.error || body.message || `Failed to load projects (HTTP ${res.status})`);
            }
            const list = (body.projects || [])
                .map((p) => ({ key: p.key, name: p.name }))
                .filter((p) => p.key)
                .sort((a, b) => a.key.localeCompare(b.key));
            setBugProjects(list);
            setBugProjectsLoaded(true);
        } catch (e) {
            setBugError(e.message);
        }
    }

    function toggleBug() {
        const next = !bugOpen;
        setBugOpen(next);
        if (next && !bugProjectsLoaded) loadProjects();
    }

    // Pre-fill and open the "Report a bug" form from a failed replay step, so
    // the tester doesn't have to retype what already failed.
    function createBugFromFailure() {
        if (!failureOverlay) return;
        const { label, steps: runSteps, failed } = failureOverlay;
        const failLines = failed.map((f) => {
            const step = runSteps[f.index];
            const desc = step ? humanStep(step) : `step ${f.index + 1} (${f.action})`;
            return `${f.index + 1}. ${desc} — FAILED${f.error ? `: ${f.error}` : ''}`;
        }).join('\n');
        setBugSummary(`RapidChat test "${label}" failed — ${failed.length} of ${runSteps.length} step(s)`);
        setBugDescription((d) => {
            const note = `Automated test "${label}" failed during replay in app "${appName}":\n${failLines}`;
            return d ? `${d}\n\n${note}` : note;
        });
        setBugSteps((prev) => {
            const block = humanizeSteps(runSteps, countNumberedLines(prev) + 1);
            return prev ? `${prev}\n${block}` : block;
        });
        if (!bugProjectsLoaded) loadProjects();
        setBugOpen(true);
        setFailureOverlay(null);
    }

    function useSelectionForBug() {
        const s = readAppSelection();
        if (!s) { setBugError('No text selected in the app — highlight something first.'); return; }
        setBugError('');
        setBugDescription((d) => (d ? `${d}\n\n${s}` : s));
    }

    async function submitBug() {
        if (!bugProject) { setBugError('Pick a JIRA project first.'); return; }
        if (!bugSummary.trim()) { setBugError('Enter a short summary.'); return; }
        setBugBusy(true);
        setBugError('');
        setBugResult(null);
        try {
            const token = await getToken();
            const res = await fetch('/api/jira/report-bug', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    projectKey: bugProject,
                    summary: bugSummary.trim(),
                    description: bugDescription.trim(),
                    stepsToReproduce: bugSteps.trim(),
                    priority: bugPriority || undefined,
                    labels: ['rapidchat-tester'],
                    appName,
                    appUrl: bugIncludeCtx ? currentAppUrl() : undefined,
                }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || body.success === false) {
                throw new Error(body.error || `Create failed (HTTP ${res.status})`);
            }
            setBugResult({ key: body.key, link: body.link });
            setBugSummary('');
            setBugDescription('');
            setBugSteps('');
        } catch (e) {
            setBugError(e.message);
        } finally {
            setBugBusy(false);
        }
    }

    const bugFiltered = bugProjects.filter((p) => {
        const q = bugFilter.trim().toLowerCase();
        return !q || `${p.key} ${p.name}`.toLowerCase().includes(q);
    });

    // Record the tester's interactions in the app and drop them into the
    // "Steps to reproduce" box as a plain-English numbered list. Uses a separate
    // Recorder instance from the test-case recorder so the two never collide.
    function startBugRecording() {
        if (recording) { setBugError('Stop the test recording above before recording bug steps.'); return; }
        try {
            const { win, doc } = getWinDoc();
            const rec = new Recorder({ win, doc });
            rec.start();
            bugRecorderRef.current = rec;
            setBugRecording(true);
            setBugError('');
        } catch (e) {
            setBugError('Cannot record: ' + e.message);
        }
    }

    function stopBugRecording() {
        const rec = bugRecorderRef.current;
        if (!rec) return;
        const recorded = rec.stop();
        bugRecorderRef.current = null;
        setBugRecording(false);
        if (!recorded.length) { setBugError('No interactions were captured — try again and click/type in the app.'); return; }
        setBugError('');
        setBugSteps((prev) => {
            const existing = (prev || '').replace(/\s+$/, '');
            const block = humanizeSteps(recorded, countNumberedLines(existing) + 1);
            return existing ? `${existing}\n${block}` : block;
        });
    }

    // Safety net: stop any live recorder if the overlay unmounts mid-capture.
    useEffect(() => () => {
        try { if (recorderRef.current) recorderRef.current.stop(); } catch { /* noop */ }
        try { if (bugRecorderRef.current) bugRecorderRef.current.stop(); } catch { /* noop */ }
    }, []);

    // ---- gate rendering -----------------------------------------------------
    if (authz === 'checking') {
        return <div style={st.center}>Checking access…</div>;
    }
    if (authz === 'denied') {
        return (
            <div style={st.center}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
                    <h2 style={{ margin: '0 0 8px' }}>You don’t have RapidChat access</h2>
                    <p style={{ color: '#9ca3af', marginBottom: 16 }}>RapidChat tester mode is available to this app’s owner, its co-admins, and users added as testers. Ask an app admin to add you under “👥 Users”.</p>
                    <button type="button" style={st.linkBtn} onClick={() => navigate('/')}>← Back to launch page</button>
                </div>
            </div>
        );
    }

    // Anchor the popup to the icon: above it when the icon sits in the lower
    // half of the screen, below it otherwise, and clamp horizontally on-screen.
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    const panelLeft = clamp(pos.left, 8, Math.max(8, vw - 380));
    const below = pos.top < vh / 2;
    const panelStyle = {
        ...st.panel,
        left: panelLeft,
        ...(below ? { top: pos.top + 60 } : { bottom: vh - pos.top + 12 }),
    };

    return (
        <div style={st.root}>
            {/* The deployed app under test */}
            <iframe
                ref={iframeRef}
                title={`app-under-test-${appName}`}
                src={src}
                style={st.appFrame}
            />

            {/* Popup panel (parent document — never recorded) */}
            {panelOpen && (
                <div style={panelStyle} data-lyric-tester-ui>
                    <div style={st.panelHead}>
                        <span style={{ fontWeight: 700 }}>🤖 RapidChat · Tester</span>
                        <button type="button" style={st.iconBtn} onClick={() => setPanelOpen(false)} title="Collapse">▾</button>
                    </div>

                    <div style={st.appTag}>app: <code>{appName}</code></div>

                    <div style={st.controls}>
                        {!recording ? (
                            <button type="button" style={{ ...st.btn, ...st.btnRecord }} disabled={busy} onClick={startRecording}>● Record</button>
                        ) : (
                            <button type="button" style={{ ...st.btn, ...st.btnStop }} onClick={stopRecording}>■ Stop</button>
                        )}
                        <button type="button" style={{ ...st.btn, ...st.btnReplay }} disabled={busy || recording || steps.length === 0} onClick={() => replay()}>▶ Replay</button>
                        <button type="button" style={{ ...st.btn, ...st.btnGhost }} disabled={busy || recording || steps.length === 0} onClick={clearSteps} title="Clear the recorded steps so you can start a fresh test">🗑 Clear</button>
                        <button type="button" style={{ ...st.btn, ...st.btnGhost }} disabled={busy || recording} onClick={reloadApp}>↺ Reload app</button>
                    </div>

                    <div style={st.status}>{busy && '⏳ '}{status}</div>

                    {/* Save current recording */}
                    <div style={st.saveRow}>
                        <input
                            style={st.input}
                            placeholder="name this test case"
                            value={caseName}
                            onChange={(e) => setCaseName(e.target.value)}
                        />
                        <button type="button" style={{ ...st.btn, ...st.btnPrimary }} disabled={steps.length === 0} onClick={saveCase}>💾 Save</button>
                    </div>

                    {/* Recorded steps (collapsible) */}
                    <button type="button" style={st.disclosure} onClick={() => setShowSteps((s) => !s)}>
                        {showSteps ? '▾' : '▸'} recorded steps ({steps.length})
                    </button>
                    {showSteps && (
                        <pre style={st.stepsPre}>{steps.length ? JSON.stringify(steps, null, 2) : '// nothing recorded yet'}</pre>
                    )}

                    {/* Replay report */}
                    {report.length > 0 && (
                        <div style={st.report}>
                            {report.map((r) => (
                                <div key={r.index} style={{ color: r.ok ? '#16a34a' : '#dc2626', fontSize: 12 }}>
                                    {r.ok ? '✓' : '✗'} {r.index}. {r.action} {r.target ? `· ${r.target}` : ''} {r.error ? `— ${r.error}` : ''}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Saved cases for this app */}
                    <div style={st.casesHead}>
                        <span>saved tests ({myCases.length})</span>
                        <span style={{ display: 'flex', gap: 6 }}>
                            <button type="button" style={st.tinyBtn} onClick={exportCases} title="Export all cases">⇩</button>
                            <button type="button" style={st.tinyBtn} onClick={() => fileRef.current && fileRef.current.click()} title="Import cases">⇧</button>
                            <input ref={fileRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={(e) => { importCases(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                        </span>
                    </div>
                    <div style={st.casesList}>
                        {myCases.length === 0 && <div style={{ color: '#9ca3af', fontSize: 12, padding: '4px 0' }}>none yet</div>}
                        {myCases.map((c) => (
                            <div key={c.id} style={st.caseRow}>
                                <span style={st.caseName} title={c.name}>{c.name}</span>
                                <span style={{ color: '#9ca3af', fontSize: 11 }}>{(c.steps || []).length}</span>
                                <button type="button" style={st.tinyBtn} title="Load" onClick={() => loadCase(c)}>load</button>
                                <button type="button" style={st.tinyBtn} title="Load & replay" disabled={busy} onClick={() => { loadCase(c); replay(c.steps, c.name); }}>▶</button>
                                <button type="button" style={{ ...st.tinyBtn, color: '#dc2626' }} title="Delete" onClick={() => deleteCase(c.id)}>✕</button>
                            </div>
                        ))}
                    </div>

                    {/* ---- Ask AI ---- */}
                    <div style={st.aiDivider} />
                    <div style={st.aiHead}>💬 Ask AI</div>

                    <div ref={aiThreadRef} style={st.aiThread}>
                        {aiMsgs.length === 0 && (
                            <div style={{ color: '#9ca3af', fontSize: 12 }}>
                                Select text in the app and click “Use selection”, or just type a question below.
                            </div>
                        )}
                        {aiMsgs.map((m, i) => (
                            <div key={i} style={m.role === 'user' ? st.msgUser : st.msgAi}>
                                {m.role === 'user' && m.ctx ? (
                                    <div style={st.msgCtx}>“{m.ctx.length > 160 ? m.ctx.slice(0, 160) + '…' : m.ctx}”</div>
                                ) : null}
                                <div style={{ color: m.err ? '#fca5a5' : undefined, whiteSpace: 'pre-wrap' }}>{m.text}</div>
                            </div>
                        ))}
                        {aiBusy && <div style={st.msgAi}>… thinking</div>}
                    </div>

                    {aiSelection && (
                        <div style={st.ctxBox}>
                            <div style={st.ctxLabel}>
                                <span>selected context</span>
                                <button type="button" style={st.tinyBtn} onClick={() => setAiSelection('')} title="Clear selection">clear</button>
                            </div>
                            <div style={st.ctxText}>{aiSelection.length > 220 ? aiSelection.slice(0, 220) + '…' : aiSelection}</div>
                        </div>
                    )}

                    {aiError && <div style={st.aiError}>{aiError}</div>}

                    <div style={st.aiInputRow}>
                        <button type="button" style={{ ...st.btn, ...st.btnGhost }} onClick={grabSelection} title="Grab the text currently highlighted in the app">⧉ Use selection</button>
                    </div>
                    <div style={st.aiPromptRow}>
                        <textarea
                            style={st.aiTextarea}
                            rows={2}
                            placeholder="Ask about the selection or anything else…  (Enter to send)"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!aiBusy) askAi(); } }}
                        />
                        <button type="button" style={{ ...st.btn, ...st.btnPrimary, ...(aiBusy ? { opacity: 0.6 } : {}) }} disabled={aiBusy} onClick={askAi}>Ask</button>
                    </div>

                    {/* ---- Report a bug ---- */}
                    <div style={st.aiDivider} />
                    <button type="button" style={st.bugToggle} onClick={toggleBug}>
                        {bugOpen ? '▾' : '▸'} 🐞 Report a bug
                    </button>

                    {bugOpen && (
                        <div style={st.bugBox}>
                            {/* Project picker (searchable) */}
                            <label style={st.fieldLabel}>JIRA project *</label>
                            <div style={{ position: 'relative', marginBottom: 8 }}>
                                <input
                                    style={st.input}
                                    placeholder={bugProjectsLoaded ? 'search project by key or name…' : 'loading projects…'}
                                    value={bugFilter}
                                    onChange={(e) => { setBugFilter(e.target.value); setBugListOpen(true); }}
                                    onFocus={() => setBugListOpen(true)}
                                />
                                {bugProject && (
                                    <div style={st.pickedProject}>selected: <b>{bugProject}</b> <button type="button" style={st.tinyBtn} onClick={() => { setBugProject(''); setBugFilter(''); }}>change</button></div>
                                )}
                                {bugListOpen && bugFiltered.length > 0 && (
                                    <div style={st.projectList}>
                                        {bugFiltered.slice(0, 12).map((p) => {
                                            const pick = () => { setBugProject(p.key); setBugFilter(`${p.key} — ${p.name}`); setBugListOpen(false); };
                                            return (
                                                <div
                                                    key={p.key}
                                                    role="button"
                                                    tabIndex={0}
                                                    style={st.projectRow}
                                                    onClick={pick}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
                                                >
                                                    <b>{p.key}</b> — {p.name}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <label style={st.fieldLabel}>Summary *</label>
                            <input style={{ ...st.input, marginBottom: 8 }} placeholder="short bug title" value={bugSummary} onChange={(e) => setBugSummary(e.target.value)} />

                            <label style={st.fieldLabel}>Description</label>
                            <textarea style={{ ...st.aiTextarea, marginBottom: 6, width: '100%' }} rows={3} placeholder="what's wrong…" value={bugDescription} onChange={(e) => setBugDescription(e.target.value)} />
                            <div style={{ marginBottom: 8 }}>
                                <button type="button" style={{ ...st.btn, ...st.btnGhost }} onClick={useSelectionForBug} title="Append the text highlighted in the app">⧉ Use selection</button>
                            </div>

                            <div style={st.bugStepsHead}>
                                <label style={{ ...st.fieldLabel, marginBottom: 0 }}>Steps to reproduce</label>
                                {!bugRecording ? (
                                    <button
                                        type="button"
                                        style={st.recStepsBtn}
                                        disabled={recording}
                                        title={recording ? 'Stop the test recording first' : 'Record your clicks/typing in the app and add them as steps'}
                                        onClick={startBugRecording}
                                    >
                                        ● Record steps
                                    </button>
                                ) : (
                                    <button type="button" style={{ ...st.recStepsBtn, ...st.recStepsBtnActive }} onClick={stopBugRecording}>
                                        ■ Stop &amp; add
                                    </button>
                                )}
                            </div>
                            {bugRecording && (
                                <div style={st.recHint}>● recording — interact with the app, then press “Stop &amp; add”.</div>
                            )}
                            <textarea style={{ ...st.aiTextarea, marginBottom: 8, width: '100%' }} rows={3} placeholder={'1. …\n2. …\n3. …\n\n…or press “Record steps” and interact with the app.'} value={bugSteps} onChange={(e) => setBugSteps(e.target.value)} />

                            <div style={st.bugRow}>
                                <label style={st.fieldLabel}>Priority</label>
                                <select style={st.select} value={bugPriority} onChange={(e) => setBugPriority(e.target.value)}>
                                    {['Highest', 'High', 'Medium', 'Low', 'Lowest'].map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            <label style={st.checkRow}>
                                <input type="checkbox" checked={bugIncludeCtx} onChange={(e) => setBugIncludeCtx(e.target.checked)} />
                                include app name + current URL
                            </label>

                            {bugError && <div style={st.aiError}>{bugError}</div>}
                            {bugResult && (
                                <div style={st.bugOk}>
                                    ✅ Created <a href={bugResult.link} target="_blank" rel="noreferrer" style={st.link}>{bugResult.key}</a>
                                </div>
                            )}

                            <button type="button" style={{ ...st.btn, ...st.btnBug, width: '100%', marginTop: 6, ...(bugBusy ? { opacity: 0.6 } : {}) }} disabled={bugBusy} onClick={submitBug}>
                                {bugBusy ? '⏳ Creating…' : '🐞 Create Bug ticket'}
                            </button>
                        </div>
                    )}

                    <button type="button" style={st.exit} onClick={exitTester}>⨯ Exit Tester Mode</button>
                </div>
            )}

            {/* Draggable floating icon */}
            <button
                type="button"
                data-lyric-tester-ui
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                title="RapidChat Tester — drag to move, click to open"
                style={{ ...st.fab, left: pos.left, top: pos.top, ...(recording ? st.fabRec : {}) }}
            >
                {recording ? '●' : '🤖'}
            </button>

            {/* Replay-failure overlay — surfaces failed step(s) with a direct path to file a bug */}
            {failureOverlay && (
                <div style={st.failBackdrop} data-lyric-tester-ui>
<div
    style={st.failBox}
    role="dialog"
    aria-modal="true"
    aria-label={`Replay failures for ${failureOverlay.label}`}
>
                        <div style={st.failHead}>⚠️ “{failureOverlay.label}” had {failureOverlay.failed.length} failing step{failureOverlay.failed.length === 1 ? '' : 's'}</div>
                        <div style={st.failList}>
                            {failureOverlay.failed.map((f) => {
                                const step = failureOverlay.steps[f.index];
                                return (
                                    <div key={f.index} style={st.failItem}>
                                        <div style={{ fontWeight: 600 }}>Step {f.index + 1}: {step ? humanStep(step) : f.action}</div>
                                        {f.error && <div style={st.failError}>{f.error}</div>}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={st.failActions}>
                            <button type="button" style={{ ...st.btn, ...st.btnGhost }} onClick={() => setFailureOverlay(null)}>Dismiss</button>
                            <button type="button" style={{ ...st.btn, ...st.btnBug }} onClick={createBugFromFailure}>🐞 Create bug from this</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const st = {
    root: { position: 'fixed', inset: 0, background: '#0b0b0f' },
    appFrame: { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: '#fff' },
    center: { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e5e7eb', background: '#0b0b0f', fontFamily: 'system-ui, sans-serif' },
    linkBtn: { background: '#1f2937', color: '#93c5fd', border: '1px solid #374151', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 600 },

    fab: {
        position: 'fixed', width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'grab',
        background: 'linear-gradient(135deg,#6d28d9,#2563eb)', color: '#fff', fontSize: 22,
        boxShadow: '0 6px 20px rgba(0,0,0,.45)', zIndex: 2147483000, touchAction: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none',
    },
    fabRec: { background: 'linear-gradient(135deg,#dc2626,#b91c1c)', animation: 'none' },

    panel: {
        position: 'fixed', width: 360, maxHeight: '78vh', overflowY: 'auto', zIndex: 2147483000,
        background: '#111827', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,.5)', padding: 12,
        fontFamily: 'system-ui, sans-serif', fontSize: 13,
    },
    panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
    appTag: { color: '#9ca3af', fontSize: 12, marginBottom: 8 },
    controls: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
    status: { fontSize: 12, color: '#cbd5e1', minHeight: 18, marginBottom: 8, wordBreak: 'break-word' },
    saveRow: { display: 'flex', gap: 6, marginBottom: 8 },
    input: { flex: 1, minWidth: 0, background: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '6px 8px', fontSize: 12 },

    btn: { padding: '6px 10px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    btnRecord: { background: '#7f1d1d', color: '#fecaca', borderColor: '#b91c1c' },
    btnStop: { background: '#b91c1c', color: '#fff' },
    btnReplay: { background: '#064e3b', color: '#a7f3d0', borderColor: '#059669' },
    btnGhost: { background: '#1f2937', color: '#cbd5e1', borderColor: '#374151' },
    btnPrimary: { background: '#1e3a8a', color: '#bfdbfe', borderColor: '#2563eb' },

    disclosure: { background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', padding: '4px 0', fontSize: 12 },
    stepsPre: { background: '#0b1220', border: '1px solid #374151', borderRadius: 6, padding: 8, maxHeight: 160, overflow: 'auto', fontSize: 11, margin: '0 0 8px' },
    report: { background: '#0b1220', border: '1px solid #374151', borderRadius: 6, padding: 8, maxHeight: 140, overflow: 'auto', marginBottom: 8 },

    casesHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#9ca3af', margin: '6px 0 4px' },
    casesList: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 },
    caseRow: { display: 'flex', alignItems: 'center', gap: 6, background: '#0b1220', border: '1px solid #1f2937', borderRadius: 6, padding: '4px 6px' },
    caseName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 },

    iconBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 16 },
    tinyBtn: { background: '#1f2937', color: '#cbd5e1', border: '1px solid #374151', borderRadius: 5, padding: '2px 6px', cursor: 'pointer', fontSize: 11 },
    exit: { width: '100%', marginTop: 4, background: '#1f2937', color: '#fca5a5', border: '1px solid #374151', borderRadius: 6, padding: '6px 0', cursor: 'pointer', fontSize: 12, fontWeight: 600 },

    aiDivider: { height: 1, background: '#374151', margin: '10px 0 8px' },
    aiHead: { fontWeight: 700, fontSize: 13, marginBottom: 6 },
    aiThread: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto', padding: 6, background: '#0b1220', border: '1px solid #1f2937', borderRadius: 8, marginBottom: 8 },
    msgUser: { alignSelf: 'flex-end', maxWidth: '90%', background: '#1e3a8a', color: '#e0ecff', borderRadius: '10px 10px 2px 10px', padding: '6px 8px', fontSize: 12 },
    msgAi: { alignSelf: 'flex-start', maxWidth: '90%', background: '#111827', color: '#e5e7eb', border: '1px solid #374151', borderRadius: '10px 10px 10px 2px', padding: '6px 8px', fontSize: 12 },
    msgCtx: { fontStyle: 'italic', color: '#93c5fd', borderLeft: '2px solid #3b82f6', paddingLeft: 6, marginBottom: 4, fontSize: 11 },

    ctxBox: { background: '#0b1220', border: '1px solid #3b82f6', borderRadius: 8, padding: 8, marginBottom: 8 },
    ctxLabel: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#93c5fd', marginBottom: 4 },
    ctxText: { fontSize: 12, color: '#cbd5e1', whiteSpace: 'pre-wrap', maxHeight: 90, overflowY: 'auto' },

    aiError: { color: '#fca5a5', fontSize: 12, marginBottom: 6 },
    aiInputRow: { display: 'flex', gap: 6, marginBottom: 6 },
    aiPromptRow: { display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 8 },
    aiTextarea: { flex: 1, minWidth: 0, resize: 'vertical', background: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' },

    bugToggle: { background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', padding: '4px 0', fontSize: 13, fontWeight: 700, textAlign: 'left', width: '100%' },
    bugBox: { background: '#0b1220', border: '1px solid #1f2937', borderRadius: 8, padding: 10, marginBottom: 8 },
    fieldLabel: { display: 'block', fontSize: 11, color: '#9ca3af', marginBottom: 3 },
    pickedProject: { fontSize: 11, color: '#a7f3d0', marginTop: 4 },
    projectList: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 2, maxHeight: 180, overflowY: 'auto', background: '#111827', border: '1px solid #374151', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,.5)' },
    projectRow: { padding: '6px 8px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #1f2937', color: '#e5e7eb' },
    bugRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
    bugStepsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 },
    recStepsBtn: { background: '#7f1d1d', color: '#fecaca', border: '1px solid #b91c1c', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
    recStepsBtnActive: { background: '#b91c1c', color: '#fff' },
    recHint: { fontSize: 11, color: '#fca5a5', marginBottom: 6 },
    select: { background: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '5px 8px', fontSize: 12 },
    checkRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#cbd5e1', marginBottom: 8 },
    btnBug: { background: '#7c2d12', color: '#fed7aa', borderColor: '#ea580c' },
    bugOk: { fontSize: 12, color: '#a7f3d0', marginBottom: 6 },
    link: { color: '#93c5fd', fontWeight: 700 },

    failBackdrop: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 2147483001,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    },
    failBox: {
        width: 360, maxWidth: '100%', maxHeight: '80vh', overflowY: 'auto',
        background: '#111827', color: '#e5e7eb', border: '1px solid #ea580c', borderRadius: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,.6)', padding: 14, fontFamily: 'system-ui, sans-serif', fontSize: 13,
    },
    failHead: { fontWeight: 700, marginBottom: 10, color: '#fca5a5' },
    failList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, maxHeight: 260, overflowY: 'auto' },
    failItem: { background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 8 },
    failError: { color: '#fca5a5', fontSize: 12, marginTop: 3 },
    failActions: { display: 'flex', gap: 8 },
};
